import React from "react";
import "../../App.css";

/**
 * ModalTableInput Component
 * A flexible table input component for modal forms
 *
 * @param {Array} headers - Array of header objects with { key, label, width, required, placeholder }
 * @param {Array} rows - Array of row data objects
 * @param {Function} onRowChange - Callback when row data changes (index, field, value)
 * @param {Function} onRowRemove - Callback when row is removed (index)
 * @param {Function} onRowAdd - Callback when new row is added
 * @param {Function} renderCell - Custom cell renderer (header, row, index)
 * @param {boolean} canRemove - Whether rows can be removed (deprecated - use actions column in headers)
 * @param {string} addButtonText - Text for add button
 * @param {React.ReactNode} footerContent - Optional footer content below table
 */
const ModalTableInput = ({
  headers = [],
  rows = [],
  onRowChange,
  onRowRemove,
  onRowAdd,
  renderCell,
  canRemove = true, // Keep for backward compatibility but not used
  addButtonText = "Add Another Entry",
  footerContent = null,
  title,
  layout = "table", // "table" | "card"
  cardGridColumns = "1fr 1fr 1fr 1fr"
}) => {
  // Generate grid template columns based on header widths
  const getGridTemplate = () => {
    if (headers.length === 0) return "1fr";
    const columns = headers.map((h) => h.width || "1fr").join(" ");
    return columns;
  };

  if (layout === "card") {
    return (
      <div className="modal-table-input-container">
        {rows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className="modal-input-card"
            style={{
              background: "#fff",
              borderRadius: "12px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
              border: "1px solid #e2e8f0",
              padding: "1.25rem",
              marginBottom: "1.25rem"
            }}
          >
            {/* Card Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", borderBottom: "1px solid #f1f5f9", paddingBottom: "0.75rem" }}>
              <h4 style={{ margin: 0, color: "#475569", fontSize: "1.05rem", fontWeight: "600" }}>
                {title ? `${title} #${rowIndex + 1}` : `Entry #${rowIndex + 1}`}
              </h4>
              
              {/* Delete button logic if actions exist in header */}
              {headers.some(h => h.key === 'actions') && onRowRemove && (
                <button
                  type="button"
                  onClick={() => onRowRemove(rowIndex)}
                  className="modal-table-remove-btn"
                  title="Remove this entry"
                  style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                >
                  <i className="fa-solid fa-trash" style={{ fontSize: "0.85rem" }}></i>
                </button>
              )}
            </div>

            {/* Card Body */}
            <div style={{ display: "grid", gridTemplateColumns: cardGridColumns, gap: "1rem" }}>
              {headers
                .filter(header => header.key !== 'actions')
                .map((header, colIndex) => (
                  <div key={colIndex} className="modal-card-field">
                    <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "700", color: "#334155", marginBottom: "0.4rem" }}>
                      {header.label}
                      {header.required && <span style={{ color: "#ef4444", marginLeft: "0.25rem" }}>*</span>}
                    </label>
                    {renderCell ? (
                      renderCell(header, row, rowIndex)
                    ) : (
                      <input
                        type="text"
                        value={row[header.key] || ""}
                        onChange={(e) => onRowChange(rowIndex, header.key, e.target.value)}
                        className="modal-table-input"
                        placeholder={header.placeholder || ""}
                      />
                    )}
                  </div>
                ))}
            </div>
          </div>
        ))}

        {/* Footer Content */}
        {footerContent && (
          <div className="modal-table-footer" style={{ marginBottom: "1rem" }}>{footerContent}</div>
        )}

        {/* Add Row Button */}
        {onRowAdd && (
          <button
            type="button"
            onClick={onRowAdd}
            className="modal-table-add-btn"
          >
            <i className="fa-solid fa-plus"></i> {addButtonText}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="modal-table-input-container">
      <div className="modal-table-input">
        {/* Table Header */}
        <div
          className="modal-table-header"
          style={{ gridTemplateColumns: getGridTemplate() }}
        >
          {headers.map((header, index) => (
            <div key={index} className="modal-table-cell" title={header.title}>
              {header.required && (
                <i className="fa-solid fa-asterisk requiredIcon"></i>
              )}
              {header.required && "\u00A0"}
              {header.label}
            </div>
          ))}
        </div>

        {/* Table Rows */}
        {rows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className="modal-table-row"
            data-row-index={rowIndex}
            style={{ gridTemplateColumns: getGridTemplate() }}
          >
            {headers.map((header, colIndex) => (
              <div
                key={colIndex}
                className="modal-table-cell"
                data-label={header.label}
              >
                {renderCell ? (
                  renderCell(header, row, rowIndex)
                ) : (
                  <input
                    type="text"
                    value={row[header.key] || ""}
                    onChange={(e) =>
                      onRowChange(rowIndex, header.key, e.target.value)
                    }
                    className="modal-table-input"
                    placeholder={header.placeholder || ""}
                  />
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Footer Content (e.g., total count display) */}
      {footerContent && (
        <div className="modal-table-footer">{footerContent}</div>
      )}

      {/* Add Row Button */}
      {onRowAdd && (
        <button
          type="button"
          onClick={onRowAdd}
          className="modal-table-add-btn"
        >
          <i className="fa-solid fa-plus"></i> {addButtonText}
        </button>
      )}
    </div>
  );
};

export default ModalTableInput;